import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar, CheckCircle2, ChevronLeft, ChevronRight,
  Filter, GripVertical, Send, Search, User, X,
  ThumbsUp, RotateCcw, Archive, Play, Lock, Eye,
  Clock, Tag, AlertTriangle, Layers
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Textarea } from "./ui/textarea.jsx";

// ── 4-column workflow ──────────────────────────────────────────────────────────
const COLUMNS = [
  { key: "todo",        label: "To Do",       color: "#6366f1", bg: "bg-[#f3eeff]",  dot: "bg-indigo-400"  },
  { key: "in_progress", label: "In Progress", color: "#f59e0b", bg: "bg-amber-50",   dot: "bg-amber-400"   },
  { key: "in_review",   label: "In Review",   color: "#8b5cf6", bg: "bg-violet-50",  dot: "bg-violet-400"  },
  { key: "approved",    label: "Approved",    color: "#10b981", bg: "bg-emerald-50", dot: "bg-emerald-400" },
];

const PRIORITY_META = {
  1: { label: "Low",    cls: "bg-slate-100 text-slate-600"  },
  2: { label: "Medium", cls: "bg-amber-100 text-amber-700"  },
  3: { label: "High",   cls: "bg-rose-100 text-rose-700"    },
};

const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
const initials = (v) => (v || "U").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

// ── Reject modal ───────────────────────────────────────────────────────────────
const RejectModal = ({ open, taskTitle, loading, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setReason(""); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <RotateCcw className="w-4 h-4" /> Return for Changes
          </DialogTitle>
          <DialogDescription>
            "{taskTitle}" will go back to In Progress and the developer will be notified.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (reason.trim()) { onConfirm(reason.trim()); setReason(""); } }} className="space-y-4 pt-1">
          <div className="space-y-1">
            <label className="text-sm font-medium">Reason <span className="text-rose-500">*</span></label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="What needs to be fixed or improved?" rows={3} autoFocus required />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setReason(""); onClose(); }}>Cancel</Button>
            <Button type="submit" variant="destructive" disabled={!reason.trim() || loading}>
              {loading ? "Returning…" : "Return to Developer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Edit modal (manager/lead only) ────────────────────────────────────────────
const EditModal = ({ task, members, loading, onClose, onSave }) => {
  const [title,    setTitle]    = useState(task?.title || "");
  const [priority, setPriority] = useState(String(task?.priority || 2));
  const [assignee, setAssignee] = useState(task?.assignee_id || "unassigned");
  const [sts,      setSts]      = useState(task?.status || "todo");
  if (!task) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
          <DialogDescription>Update task details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => {
          e.preventDefault();
          onSave(task.id, { title: title.trim(), priority: Number(priority), assignee_id: assignee === "unassigned" ? null : assignee, status: sts });
        }} className="space-y-4">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" required />
          <div className="grid gap-3 sm:grid-cols-2">
            <Select value={sts} onValueChange={setSts}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Low</SelectItem>
                <SelectItem value="2">Medium</SelectItem>
                <SelectItem value="3">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                  {m.full_name || m.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>{loading ? "Saving…" : "Save Changes"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Main Kanban ────────────────────────────────────────────────────────────────
const ModernKanban = ({
  tasks = [],
  members = [],
  sprints = [],
  currentUserId = null,
  canManageBoard = false,   // PM, Scrum Master, Team Lead
  canApprove = false,       // PM, Scrum Master, Team Lead (NOT Owner/Admin)
  isReadOnly = false,       // Owner / Admin — see everything, touch nothing
  onMoveTask,
  onReorderColumn,
  onUpdateTask,
  onArchiveTask,
  onTaskClick,
  busy = false,
}) => {
  const [draggingId,   setDraggingId]   = useState(null);
  const [search,       setSearch]       = useState("");
  const [priFilter,    setPriFilter]    = useState("all");
  const [asnFilter,    setAsnFilter]    = useState("all");
  const [tagFilter,    setTagFilter]    = useState("all");
  const [editTask,     setEditTask]     = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [actionBusy,   setActionBusy]  = useState(false);

  // Collect all unique tags
  const allTags = useMemo(() => {
    const s = new Set();
    tasks.forEach(t => (t.tags || []).forEach(g => s.add(g)));
    return [...s].sort();
  }, [tasks]);

  // ── Group & filter ──────────────────────────────────────────────────────────
  const byCol = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    tasks
      .filter((t) => !t.archived)
      .filter((t) => {
        const txt  = `${t.title || ""} ${t.description || ""}`.toLowerCase();
        const s    = !search.trim() || txt.includes(search.toLowerCase());
        const p    = priFilter === "all" || String(t.priority || 2) === priFilter;
        const a    = asnFilter === "all" || (asnFilter === "unassigned" ? !t.assignee_id : t.assignee_id === asnFilter);
        const tg   = tagFilter === "all" || (t.tags || []).includes(tagFilter);
        return s && p && a && tg;
      })
      .forEach((t) => { const col = g[t.status] ? t.status : "todo"; g[col].push(t); });
    COLUMNS.forEach((c) => g[c.key].sort((a, b) => (a.position || 0) - (b.position || 0)));
    return g;
  }, [tasks, search, priFilter, asnFilter, tagFilter]);

  const REJECTION_SOURCES = new Set(["in_review", "approved"]);

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  const handleDrop = async (e, toStatus, idx) => {
    e.preventDefault();
    if (!canManageBoard || isReadOnly) return;
    const taskId  = e.dataTransfer.getData("text/task-id");
    const fromSts = e.dataTransfer.getData("text/from-status");
    if (!taskId) return;
    setDraggingId(null);
    if (fromSts === toStatus) {
      const ids = byCol[toStatus].map((t) => t.id);
      const fi  = ids.indexOf(taskId);
      if (fi < 0) return;
      ids.splice(fi, 1); ids.splice(idx, 0, taskId);
      await onReorderColumn?.(toStatus, ids);
    } else if (toStatus === "in_progress" && REJECTION_SOURCES.has(fromSts) && canApprove) {
      // Rejection transition — must provide a reason; open the modal
      const task = tasks.find((t) => t.id === taskId);
      setRejectTarget({ taskId, fromStatus: fromSts, title: task?.title || "" });
    } else {
      await onMoveTask?.(taskId, toStatus, idx);
    }
  };

  const moveByKey = async (task, dir) => {
    if (!canManageBoard || isReadOnly) return;
    const ci = COLUMNS.findIndex((c) => c.key === task.status);
    const tgt = COLUMNS[ci + dir];
    if (!tgt) return;
    if (tgt.key === "in_progress" && REJECTION_SOURCES.has(task.status) && canApprove) {
      // Rejection transition — must provide a reason; open the modal
      setRejectTarget({ taskId: task.id, fromStatus: task.status, title: task.title });
    } else {
      await onMoveTask?.(task.id, tgt.key, byCol[tgt.key].length);
    }
  };

  // ── Task actions ─────────────────────────────────────────────────────────────
  const doAction = async (fn) => {
    setActionBusy(true);
    try { await fn(); } finally { setActionBusy(false); }
  };

  const handleApprove = (task) =>
    doAction(() => onMoveTask?.(task.id, "approved", byCol.approved.length));

  const handleReject = async (comment) => {
    if (!rejectTarget) return;
    await doAction(() => onMoveTask?.(rejectTarget.taskId, "in_progress", byCol.in_progress.length, comment));
    setRejectTarget(null);
  };

  const hasFilters = search || priFilter !== "all" || asnFilter !== "all" || tagFilter !== "all";

  return (
    <>
      {/* ── Observer banner ── */}
      {isReadOnly && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">
          <Eye className="h-4 w-4 shrink-0" />
          <span>You are viewing this board as an observer. Tasks are read-only.</span>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-slate-200/70 bg-white/75 p-3 shadow-sm backdrop-blur md:flex-row md:items-center md:justify-between">
        <div className="relative flex-1 min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={priFilter} onValueChange={setPriFilter}>
            <SelectTrigger className="w-[140px]">
              <Filter className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="1">Low</SelectItem>
              <SelectItem value="2">Medium</SelectItem>
              <SelectItem value="3">High</SelectItem>
            </SelectContent>
          </Select>
          <Select value={asnFilter} onValueChange={setAsnFilter}>
            <SelectTrigger className="w-[155px]">
              <User className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignees</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                  {m.full_name || m.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {allTags.length > 0 && (
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-[130px]">
                <Tag className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {hasFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={() => { setSearch(""); setPriFilter("all"); setAsnFilter("all"); setTagFilter("all"); }}>
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* ── Board (4 columns) ── */}
      <div className="grid gap-4 xl:grid-cols-4 lg:grid-cols-2 md:grid-cols-2">
        {COLUMNS.map((col) => (
          <Card key={col.key}
            className="min-h-[500px] overflow-hidden rounded-2xl"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, col.key, byCol[col.key].length)}>

            <CardHeader className={`pb-3 ${col.bg}`}>
              <CardTitle className="flex items-center justify-between text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                  {col.label}
                </span>
                <Badge variant="outline" className="font-mono">{byCol[col.key].length}</Badge>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-3 pt-3">
              <AnimatePresence initial={false}>
                {byCol[col.key].map((task, idx) => {
                  const assignee  = members.find((m) => (m.user_id || m.id) === task.assignee_id);
                  const priority  = PRIORITY_META[task.priority] || PRIORITY_META[2];
                  const isOwn     = task.assignee_id === currentUserId;
                  const canDrag   = canManageBoard && !isReadOnly && !busy;

                  // Developer action buttons
                  const showStart  = isOwn && !canManageBoard && !isReadOnly && task.status === "todo";
                  const showSubmit = isOwn && !canManageBoard && !isReadOnly && task.status === "in_progress";

                  // Approver action buttons (Lead/PM/SM — NOT Owner/Admin)
                  const showApprove = canApprove && !isReadOnly && task.status === "in_review" && !isOwn;
                  const showReject  = canApprove && !isReadOnly &&
                    (task.status === "in_review" || task.status === "approved");

                  return (
                    <motion.div key={task.id} layout
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.16 }}
                      draggable={canDrag}
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/task-id", task.id);
                        e.dataTransfer.setData("text/from-status", col.key);
                        setDraggingId(task.id);
                      }}
                      onDragEnd={() => setDraggingId(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, col.key, idx)}
                      className={`group rounded-2xl border bg-white p-4 shadow-sm
                        transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md
                        ${draggingId === task.id ? "opacity-50 ring-2 ring-indigo-300" : ""}
                        ${task.status === "in_review" ? "border-l-4 border-l-violet-400" : "border-slate-200/70"}
                        ${task.status === "approved" ? "border-l-4 border-l-emerald-400" : ""}`}>

                      {/* Title row */}
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="flex min-w-0 items-start gap-2">
                          {canManageBoard && !isReadOnly
                            ? <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 group-hover:text-slate-500" />
                            : <Lock className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" />}
                          <button
                            className="truncate text-left text-sm font-medium leading-snug hover:text-[#4B1E87] cursor-pointer"
                            onClick={() => onTaskClick ? onTaskClick(task) : (canManageBoard && !isReadOnly && setEditTask(task))}>
                            {task.title}
                          </button>
                        </div>
                        <Badge className={`shrink-0 text-[10px] ${priority.cls}`}>{priority.label}</Badge>
                      </div>

                      {/* Description */}
                      {task.description && (
                        <p className="line-clamp-2 pl-6 text-xs leading-5 text-muted-foreground mb-2">
                          {task.description}
                        </p>
                      )}

                      {/* Rejection reason */}
                      {task.rejection_comment && task.status === "in_progress" && (
                        <div className="pl-6 mb-2">
                          <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
                            <span className="font-semibold">Returned: </span>{task.rejection_comment}
                          </div>
                        </div>
                      )}

                      {/* Approved-by badge */}
                      {task.status === "approved" && task.approved_by_name && (
                        <div className="pl-6 mb-2">
                          <div className="flex items-center gap-1.5 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                            <span>
                              Approved by <strong>{task.approved_by_name}</strong>
                              {task.approved_at && <span className="text-emerald-600"> · {fmt(task.approved_at)}</span>}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Tags */}
                      {(task.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 pl-6 mb-2">
                          {task.tags.slice(0, 3).map(t => (
                            <span key={t} className="text-[10px] px-1.5 py-0 rounded-full bg-[#f3eeff] text-[#4B1E87] border border-indigo-100">{t}</span>
                          ))}
                          {task.tags.length > 3 && (
                            <span className="text-[10px] text-muted-foreground">+{task.tags.length - 3}</span>
                          )}
                        </div>
                      )}

                      {/* Meta row */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Avatar className="h-5 w-5 shrink-0">
                            <AvatarFallback className="text-[9px]">
                              {task.assignee_id ? initials(assignee?.full_name || assignee?.email) : <User className="h-3 w-3" />}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate max-w-[100px]">
                            {task.assignee_id ? (assignee?.full_name || assignee?.email || "Assigned") : "Unassigned"}
                          </span>
                        </div>
                        {task.due_date && (() => {
                          const overdue = task.status !== "approved" && new Date(task.due_date) < new Date();
                          return (
                            <span className={`flex items-center gap-1 whitespace-nowrap font-medium ${overdue ? "text-rose-600" : ""}`}>
                              {overdue ? <AlertTriangle className="h-3 w-3 shrink-0" /> : <Calendar className="h-3 w-3 shrink-0" />}
                              {fmt(task.due_date)}
                            </span>
                          );
                        })()}
                        {task.estimate_hrs != null && (
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <Clock className="h-3 w-3 shrink-0" />
                            {task.estimate_hrs}h
                          </span>
                        )}
                      </div>

                      {/* ── Action buttons ── */}

                      {/* Developer: Start Task */}
                      {showStart && (
                        <Button type="button" variant="outline" size="sm"
                          className="mt-3 w-full text-[#4B1E87] border-[#d4b8f0] hover:bg-[#f3eeff]"
                          disabled={busy || actionBusy}
                          onClick={() => doAction(() => onMoveTask?.(task.id, "in_progress", byCol.in_progress.length))}>
                          <Play className="h-3.5 w-3.5 mr-1.5" /> Start Task
                        </Button>
                      )}

                      {/* Developer: Submit for Review */}
                      {showSubmit && (
                        <Button type="button" variant="outline" size="sm"
                          className="mt-3 w-full text-violet-600 border-violet-200 hover:bg-violet-50"
                          disabled={busy || actionBusy}
                          onClick={() => doAction(() => onMoveTask?.(task.id, "in_review", byCol.in_review.length))}>
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Submit for Review
                        </Button>
                      )}

                      {/* Lead/PM/SM: Approve */}
                      {showApprove && (
                        <Button type="button" size="sm"
                          className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={busy || actionBusy}
                          onClick={() => handleApprove(task)}>
                          <ThumbsUp className="h-3.5 w-3.5 mr-1.5" /> Approve Task
                        </Button>
                      )}

                      {/* Lead/PM/SM: Return for Changes */}
                      {showReject && (
                        <Button type="button" variant="ghost" size="sm"
                          className="mt-1 w-full text-rose-500 hover:bg-rose-50"
                          disabled={busy || actionBusy}
                          onClick={() => setRejectTarget({ taskId: task.id, fromStatus: task.status, title: task.title })}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Return for Changes
                        </Button>
                      )}

                      {/* Manager: keyboard arrows + archive (hover) */}
                      {canManageBoard && !isReadOnly && (
                        <>
                          <div className="mt-3 grid grid-cols-2 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <Button type="button" variant="ghost" size="sm"
                              disabled={busy || col.key === COLUMNS[0].key}
                              onClick={() => moveByKey(task, -1)}>
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm"
                              disabled={busy || col.key === COLUMNS[COLUMNS.length - 1].key}
                              onClick={() => moveByKey(task, 1)}>
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                          {onArchiveTask && (
                            <div className="mt-1 opacity-0 transition-opacity group-hover:opacity-100">
                              <Button type="button" variant="ghost" size="sm"
                                className="w-full text-xs text-muted-foreground hover:text-rose-500"
                                disabled={busy}
                                onClick={() => onArchiveTask(task.id)}>
                                <Archive className="h-3 w-3 mr-1" /> Archive
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {byCol[col.key].length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300/60 bg-white/50 p-8 text-center text-sm text-muted-foreground">
                  {isReadOnly ? "No tasks here" : "Drop tasks here"}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Edit modal ── */}
      {editTask && (
        <EditModal task={editTask} members={members} loading={actionBusy}
          onClose={() => setEditTask(null)}
          onSave={async (id, data) => {
            setActionBusy(true);
            try { await onUpdateTask?.(id, data); setEditTask(null); }
            finally { setActionBusy(false); }
          }} />
      )}

      {/* ── Reject modal ── */}
      <RejectModal open={!!rejectTarget} taskTitle={rejectTarget?.title || ""}
        loading={actionBusy} onClose={() => setRejectTarget(null)} onConfirm={handleReject} />
    </>
  );
};

export default ModernKanban;
